import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Overlay, Popover } from 'react-bootstrap';
import { useMediaQuery } from '@mui/material';
import { Icon } from '@pwc/pwc-deals-ui';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import {
  getActualUserRole,
  getUserRole,
  getIsThirdPartyMember,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import ProjectStatePill from '../../../features/projectStatePill/ProjectStatePill';
import ProjectLoader from '../../../features/projects/ProjectLoader';
import {
  getManualRefreshEnabled,
  getOpenedProjectStatus,
} from '../../../features/project/projectSlice';

import {
  isProjectCreationInProgress,
  isProjectArchived,
  isProjectPaused,
} from '../../helpers/projectStatusesUtils';

import useMobile from '../../hooks/useMobile';
import useTabletOrMobile from '../../hooks/useTabletOrMobile';
import useUserReadability from '../../hooks/useUserReadability.ts';
import UserInitialsSnippet from '../UserInitialSnippet/UserInitialsSnippet';
import AddProjectItemsHeaderMenu from './AddProjectItemsHeaderMenu/AddProjectItemsHeaderMenu';
import './Header.scss';
import BreadcrumbsContainer from '../../../features/breadcrumbs/BreadcrumbsContainer';
import {
  getUserSettingsSelector,
  getIsDarkModeSelector,
} from '../../redux/userSettings/userSettingsSlice';
import AddProjectHeaderMenu from './AddProjectHeaderMenu/AddProjectHeaderMenu';
import Separator from '../Separator';
import RefreshProject from '../RefreshProject/RefreshProject';
import { GlobalToggle } from '../GlobalToggle';
import DefaultTheme from '../../../features/defaultTheme/DefaultTheme';
import storage from '../../helpers/storage';
import { useThemeSwitch } from '../../hooks/useThemeSwitch';
import userTokenService from '../../services/security/userTokenService';

import styles from './Header.module.scss';

/*
 * Disable transitions to make them switching smoothly
 */
const disableTransitions = () => {
  document.getElementById('root')?.classList.add(styles['no-transitions']);
};
const enableTransitions = () => {
  document.getElementById('root')?.classList.remove(styles['no-transitions']);
};

const PendoHelpIcon = ({ ...props }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const ref = useRef(null);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isMobile = useMobile();
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const USER_GUIDE_URL = isThirdPartyMember
    ? '/JunctionUserGuideTP.pdf'
    : '/JunctionUserGuide.pdf';

  const handleClick = (event) => {
    setShow(!show);
    setTarget(event.target);
    if (show === true) {
      setTimeout(() => {
        document.body.click();
      }, 200);
    }
  };
  return (
    <>
      <div
        className="pendo-help nowrap"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        aria-hidden="true"
      >
        <Icon
          name="help-question"
          width={isMobile ? 24 : 32}
          className="outline icon-help"
        />
      </div>
      <Overlay
        show={show}
        target={target}
        placement="bottom"
        rootClose
        onHide={() => {
          setShow(!show);
        }}
        container={ref.current}
      >
        <Popover
          id="popover-user"
          ref={ref}
          {...props}
          data-testid="popup-menu-user"
        >
          <Popover.Content className="popup-user">
            {isPwcUser ? (
              <div className="internal-popover-container">
                <div className="user-profile-dropdown-menu-section">
                  <div>{t('helpGuides')}</div>
                </div>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://sites.google.com/pwc.com/junction-global/how-to-guides/setting-up-a-project"
                >
                  {t('settingUpAProject')}
                </a>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://sites.google.com/pwc.com/junction-global/how-to-guides/adding-content-to-junction"
                >
                  {t('addingContentToJunction')}
                </a>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://sites.google.com/pwc.com/junction-global/how-to-guides/managing-content-on-junction"
                >
                  {t('managingContentJunction')}
                </a>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://sites.google.com/pwc.com/junction-global/how-to-guides/using-analysis-templates"
                >
                  {t('usingAnalysisTemplates')}
                </a>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://sites.google.com/pwc.com/junction-global/how-to-guides/other-features"
                >
                  {t('otherFeatures')}
                </a>
                <Separator />
                <a
                  className="external-link external-link-junction-site"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://sites.google.com/pwc.com/junction-global/home"
                >
                  {t('globalJunctionSite')}
                </a>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://sites.google.com/pwc.com/junction-global/give-feedback"
                >
                  {t('submitFeedBack')}
                </a>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://wwwpwcnetwork.pwc.myshn.net/hub?id=sc_cat_item&sys_id=ff2977811b357010bdec7bb5464bcb0f"
                >
                  {t('createSupportTicket')}
                </a>
              </div>
            ) : (
              <div className="client-popover-container">
                <div className="user-profile-dropdown-menu-section">
                  <p>{t('resourceCenter')}</p>
                </div>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href={USER_GUIDE_URL}
                >
                  {t('userGuide')}
                </a>
                <a
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-workbench-link"
                  href="https://survey.pwc.com/jfe/form/SV_3JiBQRuviQ5Khee"
                >
                  {t('submitFeedbackClient')}
                </a>
                <Separator />
                <h6 className="support-header">{t('getSupport')}</h6>
                <div className="support-text">{t('issueEncountered')}</div>
              </div>
            )}
          </Popover.Content>
        </Popover>
      </Overlay>
    </>
  );
};

const Header = ({ projectId }) => {
  const isMobile = useMobile();
  const { pathname } = useLocation();
  const switchTheme = useThemeSwitch();
  const isTabletOrMobile = useTabletOrMobile();
  const isUserReadonly = useUserReadability(projectId);
  const actualUserRole = useSelector(getActualUserRole);
  const userSettings = useSelector(getUserSettingsSelector);
  const projectStatus = useSelector(getOpenedProjectStatus);
  const isUserThemeDarkMode = useSelector(getIsDarkModeSelector);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isManualRefreshEnabled = useSelector(getManualRefreshEnabled);

  const isTabletDesktop = useMediaQuery('(min-width:768px)');

  const showGlobalToggle = useMemo(() => {
    if (pathname === '/projects' || !isTabletDesktop) {
      return false;
    }
    return actualUserRole === USER_ROLES.PWC_USER;
  }, [actualUserRole, pathname, isTabletDesktop]);

  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const getUserEmail = async () => {
      const email = await userTokenService.getUserEmail();
      setUserEmail(email);
    };

    getUserEmail();
  }, []);

  const displayPendoHelpIcon = useMemo(() => {
    if (pathname === '/') {
      return false;
    }
    if (
      (pathname !== '/projects' &&
        pathname !== '/terms-and-conditions' &&
        !pathname?.endsWith('project-disclaimer')) ||
      userEmail?.endsWith('pwc.com')
    ) {
      return true;
    }
    return false;
  }, [userEmail, pathname]);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  /*
   * Switch user theme
   */
  const switchUserTheme = useCallback(
    async (_isDarkMode) => {
      disableTransitions();
      const allowSwitchOnFail = true;
      const isSucceeded = await switchTheme(_isDarkMode, allowSwitchOnFail);
      if (isSucceeded || allowSwitchOnFail) {
        storage.set('pwc-theme', _isDarkMode ? 'theme-dark' : 'theme-light');
      }
      enableTransitions();
    },
    [switchTheme]
  );

  const handleOnToggleClick = useCallback(async () => {
    if (!isPopupVisible) {
      await switchUserTheme(!isUserThemeDarkMode);
    }
  }, [isPopupVisible, isUserThemeDarkMode, switchUserTheme]);

  const renderAddProjectHeader = () => {
    if (pathname === '/projects') {
      return <AddProjectHeaderMenu />;
    }

    if (!isUserReadonly && isPwcUser) {
      if (projectId)
        return (
          <AddProjectItemsHeaderMenu
            projectId={projectId}
            isProjectDisabled={
              isProjectArchived(projectStatus) || isProjectPaused(projectStatus)
            }
          />
        );
      return <AddProjectHeaderMenu />;
    }

    return null;
  };

  let isManualRefreshVisible = false;
  if (isManualRefreshEnabled) {
    isManualRefreshVisible = true;
    if (isMobile && pathname.includes('edit')) {
      isManualRefreshVisible = false;
    }
  }

  return (
    <React.Suspense fallback="loading...">
      <div className="header">
        <div className="header-content">
          <div className="header-content-breadcrumbs">
            <BreadcrumbsContainer key={`${isPwcUser}`} projectId={projectId} />
          </div>
          <div className="snippet-pendo-container">
            {showGlobalToggle ? <GlobalToggle /> : null}

            {renderAddProjectHeader()}
            {projectId && (
              <ProjectStatePill
                projectId={projectId}
                showWhenActive={false}
                pillText="Initiating"
                header
              />
            )}
            {isProjectCreationInProgress(projectStatus) &&
              !isTabletOrMobile && <ProjectLoader />}
            {isManualRefreshVisible &&
              isPwcUser &&
              !isUserReadonly &&
              !isProjectArchived(projectStatus) && <RefreshProject />}
            {userSettings && userSettings.loaded && (
              <>
                <DefaultTheme
                  projectId={projectId}
                  handleOnToggleClick={handleOnToggleClick}
                  setIsPopupVisible={setIsPopupVisible}
                  isPopupVisible={isPopupVisible}
                  switchUserTheme={switchUserTheme}
                />
                <UserInitialsSnippet
                  handleOnToggleClick={handleOnToggleClick}
                  isPopupVisible={isPopupVisible}
                />
              </>
            )}
            {displayPendoHelpIcon && <PendoHelpIcon />}
          </div>
        </div>
      </div>
    </React.Suspense>
  );
};

export default Header;
